/* eslint-disable no-alert */
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, withRouter, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import Moment from 'moment';
import './index.css';
import './App.scss';
import * as signalR from '@microsoft/signalr';
import Routes from './Routes';
import AuthContext from './Context/AuthContext';
import ServiceStatusContext from './Context/ServiceStatusContext';
import NotificationContext from './Context/NotificationContext';
import StatusService from './Services/StatusService';
import AuthService from './Services/AuthService';
import TokenService from './Services/TokenService';
import Navigation from './Pages/WebApp/Navigation/Navigation';
import Sidebar from './Pages/WebApp/Navigation/Sidebar';
import Footer from './Pages/WebApp/Footer/Footer';
import AppBanner from './Components/AppBanner/AppBanner';
import SystemAdminService from './Services/SystemAdminService';
import NotificationService from './Services/NotificationService';

const gT = (t, key) => t(`app-jsx${key}`);

/* let me merge this, this should work */
ReactGA.initialize('G-LR764V0GL8');
ReactGA.send({ hitType: 'pageview', page: window.location.pathname });

function App() {
  const { t } = useTranslation('app');
  const history = useHistory();
  const location = useLocation();
  const [authState, setAuthState] = useState({});
  const [serviceStatusMessages, setServiceStatusMessages] = useState([]);
  const [impersonationState, setImpersonationState] = useState({});
  const [showBanner, setShowBanner] = useState(false);
  const [count, setCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  // initial useEffect on entering App
  useEffect(() => {
    // if page is refreshed we gotta call
    async function CheckImpersonation() {
      await SystemAdminService.isImpersonating()
        .then((response) => {
          if (!response.data) return;

          const impersonated = {
            id: response.data.customerId,
            name: response.data.customerName,
          };
          TokenService.setImpersonationToken(impersonated);
          setImpersonationState(impersonated);
          setShowBanner(true);
        })
        .catch(() => {});
    }

    async function ConnectToHub() {
      let url = '';
      let accessToken = '';

      if (process.env.NODE_ENV === 'production') {
        await AuthService.getNegotiateEndpoint().then((response) => {
          url = response.data.url;
          accessToken = response.data.accessToken;
        });
      } else {
        url = '/notificationhub';
        accessToken = TokenService.getLocalAccessToken();
      }

      const connection = new signalR.HubConnectionBuilder()
        .withUrl(url, {
          accessTokenFactory: () => accessToken,
        })
        .withAutomaticReconnect()
        .build();

      connection.on('ServiceStatusUpdate', async () => {
        await StatusService.getServiceStatusMessages().then((response) => {
          setServiceStatusMessages(response.data);
        });
        // console.log('Service status update message received');
      });

      connection.on('NotificationsUpdate', async () => {
        await NotificationService.getLatestWebNotifications().then(
          (response) => {
            setNotifications(
              response.data.sort(
                (a, b) => new Date(b.dateCreated) - new Date(a.dateCreated)
              )
            );
          }
        );
      });

      await connection.start().catch(() => {});
    }

    async function GetNotifications() {
      await NotificationService.getLatestWebNotifications().then((response) => {
        setNotifications(
          response.data.sort(
            (a, b) => new Date(b.dateCreated) - new Date(a.dateCreated)
          )
        );
      });
    }

    async function GetServiceStatusMessages() {
      await StatusService.getServiceStatusMessages()
        .then((response) => {
          setServiceStatusMessages(response.data);
        })
        .catch(() => {
          setServiceStatusMessages([]);
        });
    }

    const user = TokenService.getLocalUser();
    if (!user) history.push('/login');

    GetServiceStatusMessages();
    GetNotifications();
    ConnectToHub();

    if (user.role !== 10) return;
    CheckImpersonation();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const refreshCheck = async () => {
    // Check if it's time to refresh token
    setCount(count + 1);

    const now = Moment();
    const refreshTime = Moment(TokenService.getExp()).subtract(20, 'minutes');

    if (now.isAfter(refreshTime)) {
      await AuthService.refresh()
        .then((response) => {
          TokenService.setToken(response.data);
          const user = TokenService.getLocalUser();
          setAuthState((state) => ({ ...state, user }));
        })
        .catch(() => {
          alert(gT(t, '.signed-out'));
          TokenService.removeToken();
          localStorage.clear();
          sessionStorage.clear();
          setAuthState((state) => ({ ...state, user: null }));
          history.push('/login');
        });
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => refreshCheck(), 60000); // Check every minute
    return () => clearTimeout(timer);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [count]);

  useEffect(() => {
    const user = TokenService.getLocalUser();
    if (!user) history.push('/login');

    setAuthState((state) => ({ ...state, user }));

    const now = Moment();
    const refreshTokenExp = Moment(TokenService.getExp()).add(1380, 'minute');

    if (now.isAfter(refreshTokenExp)) {
      alert(gT(t, '.signed-out-inactivity'));
      TokenService.removeToken();
      localStorage.clear();
      sessionStorage.clear();
      setAuthState((state) => ({ ...state, user: null }));
      history.push('/login');
    }

    const impersonated = TokenService.getImpersonationToken();
    if (!impersonated) setShowBanner(false);
    else {
      setImpersonationState(impersonated);
      setShowBanner(true);
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [location, history]);

  const authContextValue = useMemo(
    () => [authState, setAuthState],
    [authState]
  );

  const serviceStatusContextValue = useMemo(
    () => [serviceStatusMessages, setServiceStatusMessages],
    [serviceStatusMessages]
  );

  const notificationContextValue = useMemo(
    () => [notifications, setNotifications],
    [notifications]
  );

  const stopImpersonating = async () => {
    await SystemAdminService.stopImpersonating()
      .then(() => {
        TokenService.removeImpersonationToken();
        setImpersonationState({});
        history.push('/app/systemadmin/customers');
      })
      .catch(() => {});
  };

  const getImpersonationText = () =>
    `${gT(t, '.impersonation-text-prefix')} ${
      impersonationState?.name ?? gT(t, '.impersonation-fallback-name')
    }`;

  return (
    <div className="app">
      {Object.keys(authState).length !== 0 && (
        <AuthContext.Provider value={authContextValue}>
          <ServiceStatusContext.Provider value={serviceStatusContextValue}>
            <NotificationContext.Provider value={notificationContextValue}>
              {showBanner && (
                <AppBanner
                  text={getImpersonationText()}
                  onClose={stopImpersonating}
                  closeText={gT(t, '.impersonation-text-close')}
                />
              )}
              <div
                className={`app-main ${
                  showBanner ? 'app-main-banner-margin' : ''
                }`}
              >
                <Sidebar />
                <div className="app-content">
                  <Navigation
                    isImpersonating={Object.keys(impersonationState).length > 0}
                  />
                  <div className="app-scrollable-content">
                    <Routes />
                  </div>
                  <Footer />
                </div>
              </div>
            </NotificationContext.Provider>
          </ServiceStatusContext.Provider>
        </AuthContext.Provider>
      )}
    </div>
  );
}

export default withRouter(App);
